/**
 * GeneratorNode - AI Content Generation Node
 *
 * A node that combines:
 * - AI model selection (Claude Sonnet/Opus)
 * - Prompt input with upstream context
 * - Generation triggering and status
 * - Output streaming to downstream BlockNodes
 *
 * Design principles:
 * - Beautiful multi-layered shadows for depth
 * - Clean model picker and prompt UI
 * - Connection chips showing upstream context
 * - Status indicators (idle/generating/complete/error)
 */

import { memo, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Handle, Position, NodeResizer, useStore, type NodeProps, type Node } from '@xyflow/react';
import type { GeneratorNodeData, GeneratorConnection } from '@deep-agents/shared-types';
import { DEFAULT_MODELS } from '@deep-agents/shared-types';
import { useGeneratorNode } from '../../hooks/useGeneratorNode';
import type * as Y from 'yjs';
import { getNodeContent, getNodeLabel } from '../../utils/nodeContent';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * GeneratorNode Component
 *
 * Props come from React Flow's node data structure.
 * Generation is triggered via API call to /api/boards/:boardId/generate
 */
type GeneratorNodeType = Node<GeneratorNodeData, 'generator'>;

export const GeneratorNode = memo(({ id, data, selected, dragging }: NodeProps<GeneratorNodeType>) => {
  // Destructure data with defaults
  const {
    title = '',
    model = 'claude-sonnet-4-5-20250929',
    prompt = '',
    temperature = 0.7,
    status = 'idle',
    error,
    onTitleChange,
    onModelChange,
    onPromptChange,
    // Runtime-only props injected by BoardCanvas:
    ydoc,
    boardId,
    nodesMap,
  } = data;

  // Get edges from React Flow store
  const edges = useStore((state) => state.edges);

  // Use the generator hook for AI generation logic
  const { generate: runGeneration, status: generationStatus, error: generationError } = useGeneratorNode({
    generatorNodeId: id,
    generatorData: data,
    boardId: boardId || '',
    edges,
    nodesMap: nodesMap as Y.Map<Node>,
    ydoc: ydoc as Y.Doc,
  });

  // ==================== DYNAMIC SHADOW ====================

  /**
   * Get shadow style based on node state
   * Matches all other nodes for consistency
   */
  const getShadowStyle = () => {
    if (dragging) {
      return 'var(--shadow-elevation-high)';
    }
    if (selected) {
      return 'var(--shadow-elevation-medium)';
    }
    return 'var(--shadow-elevation-low)';
  };

  // ==================== LOCAL STATE ====================

  // Local title state (synced to parent on change)
  const [localTitle, setLocalTitle] = useState(title);

  // Local prompt state (synced to parent on change)
  const [localPrompt, setLocalPrompt] = useState(prompt);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const promptRef = useRef<HTMLTextAreaElement | null>(null);
  const promptOverlayRef = useRef<HTMLDivElement | null>(null);

  // Hover state - show/hide controls and handles
  const [isHovered, setIsHovered] = useState(false);
  const [nodesVersion, setNodesVersion] = useState(0);
  const [contentVersion, setContentVersion] = useState(0);

  // ==================== SYNC EFFECTS ====================

  // Sync local state with props when they change (from Yjs updates)
  // This ensures collaboration works - if another user changes the prompt, we see it
  useEffect(() => {
    setLocalTitle(title);
  }, [title]);

  useEffect(() => {
    setLocalPrompt(prompt);
  }, [prompt]);

  useEffect(() => {
    if (!nodesMap) return;
    let rafId = 0;
    const handleUpdate = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        setNodesVersion((prev) => prev + 1);
      });
    };
    nodesMap.observe(handleUpdate);
    return () => {
      nodesMap.unobserve(handleUpdate);
      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [nodesMap]);

  useEffect(() => {
    if (!ydoc) return;
    const handleUpdate = () => {
      setContentVersion((prev) => prev + 1);
    };
    ydoc.on('update', handleUpdate);
    return () => {
      ydoc.off('update', handleUpdate);
    };
  }, [ydoc]);

  // ==================== HANDLERS ====================

  /**
   * Handle title changes
   */
  const handleTitleChange = useCallback((newTitle: string) => {
    setLocalTitle(newTitle);
    onTitleChange?.(newTitle);
  }, [onTitleChange]);

  /**
   * Handle prompt changes
   */
  const handlePromptChange = useCallback((newPrompt: string) => {
    setLocalPrompt(newPrompt);
    onPromptChange?.(newPrompt);
  }, [onPromptChange]);

  const incomingEdges = useMemo(
    () => edges.filter((edge) => edge.target === id),
    [edges, id]
  );

  const connections = useMemo<GeneratorConnection[]>(() => {
    if (!nodesMap) return [];
    const result: GeneratorConnection[] = [];
    const seen = new Set<string>();
    for (const edge of incomingEdges) {
      const sourceNode = nodesMap.get(edge.source);
      if (!sourceNode || seen.has(sourceNode.id)) {
        continue;
      }
      seen.add(sourceNode.id);
      result.push({
        nodeId: sourceNode.id,
        nodeLabel: getNodeLabel(sourceNode),
        nodeType: sourceNode.type,
      });
    }
    return result;
  }, [incomingEdges, nodesMap, nodesVersion]);

  const mentionContentById = useMemo(() => {
    const map = new Map<string, { text: string; imageUrl?: string }>();
    if (!nodesMap) return map;
    for (const conn of connections) {
      const sourceNode = nodesMap.get(conn.nodeId);
      if (!sourceNode) continue;
      const content = getNodeContent(sourceNode, ydoc as Y.Doc | undefined);
      if (content.textContent) {
        map.set(conn.nodeId, { text: content.textContent });
        continue;
      }
      if (content.imageUrl) {
        const label = conn.nodeLabel || 'Image';
        map.set(conn.nodeId, { text: `[Image] ${label}`, imageUrl: content.imageUrl });
        continue;
      }
      map.set(conn.nodeId, { text: conn.nodeLabel || conn.nodeId });
    }
    return map;
  }, [connections, nodesMap, nodesVersion, contentVersion, ydoc]);

  const promptDisplay = useMemo<React.ReactNode[]>(() => {
    const parts: React.ReactNode[] = [];
    if (!localPrompt) return parts;
    const regex = /@{{([^}]+)}}/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(localPrompt)) !== null) {
      const before = localPrompt.slice(lastIndex, match.index);
      if (before) parts.push(before);
      const nodeId = match[1];
      const content = mentionContentById.get(nodeId);
      if (content) {
        parts.push(
          <span
            key={`${nodeId}-${match.index}`}
            className="rounded bg-purple-100 px-1 text-purple-700"
          >
            {content.text}
          </span>
        );
      } else {
        parts.push(match[0]);
      }
      lastIndex = regex.lastIndex;
    }
    const rest = localPrompt.slice(lastIndex);
    if (rest) parts.push(rest);
    return parts;
  }, [localPrompt, mentionContentById]);

  const updateMentionState = useCallback((value: string, cursorPos: number) => {
    const before = value.slice(0, cursorPos);
    const match = before.match(/(?:^|\s)@([^\s@]*)$/);
    if (!match) {
      setMentionQuery(null);
      setMentionStart(null);
      return;
    }

    if (connections.length === 0) {
      setMentionQuery(null);
      setMentionStart(null);
      return;
    }

    const query = match[1] || '';
    const start = cursorPos - query.length - 1;
    setMentionQuery(query);
    setMentionStart(start);
    setMentionIndex(0);
  }, [connections.length]);

  const filteredMentions = useMemo(() => {
    if (mentionQuery === null) return [];
    const query = mentionQuery.toLowerCase();
    return connections.filter((conn) => conn.nodeLabel.toLowerCase().includes(query));
  }, [connections, mentionQuery]);

  const insertMention = useCallback((conn: GeneratorConnection) => {
    if (mentionStart === null || !promptRef.current) return;
    const cursorPos = promptRef.current.selectionStart ?? localPrompt.length;
    const before = localPrompt.slice(0, mentionStart);
    const after = localPrompt.slice(cursorPos);
    const token = `@{{${conn.nodeId}}}`;
    const nextPrompt = `${before}${token} ${after}`;
    handlePromptChange(nextPrompt);
    setMentionQuery(null);
    setMentionStart(null);

    requestAnimationFrame(() => {
      if (!promptRef.current) return;
      const nextPos = before.length + token.length + 1;
      promptRef.current.focus();
      promptRef.current.setSelectionRange(nextPos, nextPos);
    });
  }, [handlePromptChange, localPrompt, mentionStart]);

  const handlePromptKeyDown = useCallback((event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionQuery === null || filteredMentions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setMentionIndex((prev) => (prev + 1) % filteredMentions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setMentionIndex((prev) => (prev - 1 + filteredMentions.length) % filteredMentions.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const selected = filteredMentions[mentionIndex];
      if (selected) {
        insertMention(selected);
      }
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setMentionQuery(null);
      setMentionStart(null);
    }
  }, [filteredMentions, mentionIndex, mentionQuery, insertMention]);

  const handlePromptScroll = useCallback((event: React.UIEvent<HTMLTextAreaElement>) => {
    if (!promptOverlayRef.current) return;
    promptOverlayRef.current.scrollTop = event.currentTarget.scrollTop;
    promptOverlayRef.current.scrollLeft = event.currentTarget.scrollLeft;
  }, []);

  /**
   * Handle model selection change
   */
  const handleModelChange = useCallback((newModel: string) => {
    onModelChange?.(newModel);
  }, [onModelChange]);

  /**
   * Handle generate button click
   */
  const handleGenerate = useCallback(() => {
    if (generationStatus === 'generating') return; // Prevent double-click during generation
    runGeneration();
  }, [generationStatus, runGeneration]);

  // ==================== COMPUTED VALUES ====================

  // Show controls when hovered OR selected
  const showControls = isHovered || selected;

  // Get model display name
  const selectedModel = DEFAULT_MODELS.find(m => m.id === model);
  const modelDisplayName = selectedModel?.name || 'Claude Sonnet 4.5';

  // Use generation status from hook (overrides data.status)
  const currentStatus = generationStatus || status;
  const currentError = generationError || error;

  // Status styling
  const statusColors = {
    idle: 'bg-gray-100 text-gray-600',
    generating: 'bg-indigo-100 text-indigo-600',
    complete: 'bg-green-100 text-green-600',
    error: 'bg-red-100 text-red-600',
  };

  // ==================== RENDER ====================

  return (
    <div
      className="relative transition-all duration-300"
      style={{ padding: '0 8px' }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* ==================== CONNECTION HANDLES ==================== */}
      {/* DEBUG: Always fully visible handles */}
      <Handle
        type="target"
        position={Position.Left}
        id="input"
        className="!w-4 !h-4 !bg-slate-400 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Receive context from upstream nodes"
      />
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="!w-4 !h-4 !bg-purple-500 !border-2 !border-white !opacity-100"
        style={{ top: '50%', pointerEvents: 'all', zIndex: 100 }}
        title="Send generated content to downstream nodes"
      />

      {/* ==================== FLOATING HEADER (Above Card) ==================== */}
      {/* Positioned absolutely so it doesn't affect node bounding box */}
      {/* Only visible on hover or when selected */}
      <div
        className={`
          absolute bottom-full left-0 right-0 pb-2
          transition-opacity duration-200
          ${showControls ? 'opacity-100' : 'opacity-0 pointer-events-none'}
        `}
      >
        {/* Title Input */}
        <div className="flex items-center gap-2 px-1">
          <div
            className="nodrag nopan"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <input
              type="text"
              value={localTitle}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Untitled Generator"
              className="
                px-2.5 py-1 text-[12px] font-semibold
                bg-gradient-to-r from-purple-50 to-violet-50
                rounded-lg border border-purple-100/50
                outline-none focus:border-purple-300 focus:ring-1 focus:ring-purple-200
                placeholder:text-purple-300 placeholder:font-medium
                min-w-[80px] max-w-[200px] truncate
                transition-all
                text-purple-700
              "
              style={{ width: `${Math.max(80, Math.min(200, (localTitle.length || 16) * 8))}px` }}
            />
          </div>
        </div>
      </div>

      {/* ==================== MAIN CARD CONTAINER ==================== */}
      {/* This container is draggable by default (no nodrag) */}
      <div
        className="
          relative bg-white rounded-2xl overflow-visible
          border transition-all duration-300
          border-neutral-200/60
        "
        style={{
          // Dynamic shadow based on drag/select state (matches all other nodes)
          boxShadow: getShadowStyle(),
          // Smooth shadow transition + scale effect
          transition: dragging
            ? 'box-shadow 0.2s cubic-bezier(0.4, 0, 0.2, 1), transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
            : 'box-shadow 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275), transform 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
          // Slight scale up when dragging
          transform: dragging ? 'scale(1.01)' : 'scale(1)',
          minWidth: 400,
          minHeight: 300,
        }}
      >
        {/* ==================== RESIZE HANDLES ==================== */}
        <NodeResizer
          minWidth={400}
          minHeight={300}
          isVisible={selected}
          lineClassName="border-purple-500"
          handleClassName="bg-purple-500 border-2 border-white rounded"
        />

        {/* ==================== CONTENT AREA ==================== */}
        {/* This wrapper provides padding - the padding area remains draggable */}
        <div
          className="relative px-5 pt-5 pb-4 overflow-hidden"
          style={{ height: '100%', zIndex: 1 }}
        >
          {/* ==================== MODEL SELECTOR ==================== */}
          <div className="mb-4 nodrag nopan">
            <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
              Model
            </label>
            <select
              value={model}
              onChange={(e) => handleModelChange(e.target.value)}
              className="
                w-full px-3 py-2 text-[14px] font-medium
                bg-gray-50 border border-gray-200 rounded-lg
                outline-none focus:border-purple-300 focus:ring-2 focus:ring-purple-100
                transition-all cursor-pointer
                text-black
              "
              onClick={(e) => e.stopPropagation()}
            >
              {DEFAULT_MODELS.map((modelOption) => (
                <option key={modelOption.id} value={modelOption.id}>
                  {modelOption.name} - {modelOption.description}
                </option>
              ))}
            </select>
          </div>

          {/* ==================== PROMPT TEXTAREA ==================== */}
          <div className="mb-4 nodrag nopan">
          <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-2">
            Prompt
          </label>
          <div className="relative">
            <div
              className="
                relative rounded-lg border border-gray-200 bg-gray-50
                transition-all focus-within:border-purple-300 focus-within:ring-2 focus-within:ring-purple-100
              "
            >
              <div
                ref={promptOverlayRef}
                className="
                  pointer-events-none absolute inset-0 z-0
                  px-3 py-2 text-[14px] leading-relaxed
                  text-slate-900 whitespace-pre-wrap break-words
                  overflow-hidden
                "
              >
                {promptDisplay}
              </div>
              <textarea
                ref={promptRef}
                value={localPrompt}
                onChange={(e) => {
                  handlePromptChange(e.target.value);
                  const cursorPos = e.target.selectionStart ?? e.target.value.length;
                  updateMentionState(e.target.value, cursorPos);
                }}
                onKeyDown={handlePromptKeyDown}
                onScroll={handlePromptScroll}
                placeholder="Enter your prompt here... Context from upstream nodes will be automatically included."
                className="
                  relative z-10 w-full px-3 py-2 text-[14px] leading-relaxed
                  bg-transparent text-transparent caret-purple-600
                  outline-none transition-all resize-none
                  min-h-[120px]
                  placeholder:text-gray-400
                "
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
                onBlur={() => {
                  setTimeout(() => {
                    setMentionQuery(null);
                    setMentionStart(null);
                  }, 120);
                }}
              />
            </div>

            {mentionQuery !== null && filteredMentions.length > 0 && (
                <div className="absolute left-0 top-full mt-2 w-64 rounded-lg border border-purple-200 bg-white shadow-lg z-20">
                  <div className="max-h-48 overflow-y-auto py-1">
                    {filteredMentions.map((conn, index) => (
                      <button
                        key={conn.nodeId}
                        type="button"
                        className={`
                          flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium
                          ${index === mentionIndex ? 'bg-purple-50 text-purple-700' : 'text-slate-600 hover:bg-purple-50'}
                        `}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => insertMention(conn)}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                        <span className="truncate">{conn.nodeLabel}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ==================== CONTEXT CHIPS (Incoming Connections) ==================== */}
          {connections.length > 0 && (
            <div className="bg-purple-50/50 rounded-xl p-3 border border-purple-100 mb-4">
              {/* Header with icon and label */}
              <div className="flex items-center gap-2 mb-2">
                <svg className="w-3.5 h-3.5 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
                <span className="text-[10px] font-bold text-purple-500 uppercase tracking-wider">
                  Context Sources
                </span>
              </div>

              {/* Chips showing upstream nodes */}
              <div className="flex flex-wrap gap-1.5">
                {connections.map((conn: GeneratorConnection) => (
                  <span
                    key={conn.nodeId}
                    className="
                      inline-flex items-center gap-1 px-2 py-0.5
                      bg-purple-100 text-purple-600 text-[11px] font-medium
                      rounded-md
                    "
                    title={`Context from ${conn.nodeLabel}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                    {conn.nodeLabel}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* ==================== GENERATE BUTTON ==================== */}
          <div className="flex items-center justify-between gap-3 nodrag nopan">
            <button
              onClick={handleGenerate}
              disabled={currentStatus === 'generating' || !localPrompt.trim()}
              className={`
                flex-1 flex items-center justify-center gap-2 px-4 py-2.5
                text-[14px] font-semibold rounded-lg
                transition-all
                ${currentStatus === 'generating' || !localPrompt.trim()
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white hover:from-purple-600 hover:to-indigo-600 shadow-md hover:shadow-lg'
                }
              `}
            >
              {currentStatus === 'generating' ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Generate
                </>
              )}
            </button>

            {/* Status Indicator */}
            <div className={`px-3 py-2 rounded-lg text-[11px] font-semibold ${statusColors[currentStatus]}`}>
              {currentStatus.charAt(0).toUpperCase() + currentStatus.slice(1)}
            </div>
          </div>

          {/* ==================== ERROR MESSAGE ==================== */}
          {currentStatus === 'error' && currentError && (
            <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-100">
              <p className="text-[13px] text-red-600 font-medium">{currentError}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

// Display name for React DevTools
GeneratorNode.displayName = 'GeneratorNode';
